import { last } from 'lodash';
import { findMarkWords } from '../../common/functions';

import checkst from '../assets/icons/checkst.png';
import newst from '../assets/icons/newst.png';

export const drawMixin = {
  // ***************************************************************************************
  // drawCanvas()
  // permet de dessiner le canvas
  // ***************************************************************************************
  drawCanvas() {
    const { marginTop, marginLeft } = this.props.displayParameters;
    const { fontName, displayNumberOfLines, teletext } = this.props.subtitleParameters;

    this.fontSize =
      this.width > 400
        ? teletext
          ? 18
          : 20
        : (this.width /
            (displayNumberOfLines -
              parseFloat(marginTop) / 100 -
              parseFloat(marginLeft) / 100)) *
          (teletext ? 1 : 1.2);

    this.backCtx = this.backCanvas.getContext('2d');

    // On définit les valeurs par défaut sur le backCanvas
    this.backCtx.fillStyle = '#00000000';
    this.backCtx.fillRect(0, 0, this.width, this.height);
    this.backCtx.font = `${this.fontSize}px ${teletext ? "Courier New" : fontName}`;

    this.lineHeight = 26;
  },

  // ***************************************************************************************
  // drawBoxing()
  // permet de dessiner le bandeau sur la phrase si le type est "te"
  // ***************************************************************************************
  drawBoxing(ctx, startX, startY, textWidth) {
    const { teletext, boxing } = this.props.subtitleParameters;

    if (!teletext && !boxing) {
      return;
    }

    const rect = {
      x: startX - 10,
      y: startY - this.lineHeight - 7,
      width: textWidth + 20,
      height: this.lineHeight,
    };

    // Couleur de remplissage du rectangle
    ctx.fillStyle = 'black';
    ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
  },

  // ***************************************************************************************
  // drawWord()
  // permet de dessiner le mot
  // ***************************************************************************************
  drawWord(ctx, word) {
    ctx.fillStyle = word.color;
    ctx.fillText(word.text, word.startX, word.startY);
  },

  // ***************************************************************************************
  // createCursor()
  // permet de créer le curseur
  // ***************************************************************************************
  createCursor() {
    if (this.props.readOnly || !this.state.active || !this.state.focus) {
      return;
    }

    const { subtitleParameters } = this.props;

    const { teletext, boxing, fontName } = subtitleParameters;

    const subtitle = this.subtitle;
    const { lines } = subtitle;

    const height = this.lineHeight - 2;
    const metricY = 7;

    let line = lines[this.currentLineIndex];
    if (!line) {
      if (this.currentLineIndex === 0) {
        line = subtitle.lines[this.currentLineIndex + 1];
        this.currentLineIndex++;
      } else {
        line = subtitle.lines[this.currentLineIndex - 1];
        this.currentLineIndex--;
      }
    }

    if (!line) {
      if (!subtitle.lines.length) {
        return;
      }

      line = subtitle.lines[0];
      this.currentLineIndex = 0;
      this.currentWordIndex = 0;
    }

    if (!line.words.length) {
      this.currentLine = line;
      this.currentWord = null;
      this.currentLetter = null;

      // dessiner le boxing
      if (teletext || boxing) {
        this.drawBoxing(
          this.foreCtx,
          line.startX,
          line.startY,
          this.spaceWidth
        );
      }

      // dessiner le curseur
      const x = this.getCoordinateX(line);
      const y = line.startY - this.lineHeight - metricY;

      // Dessiner le rectangle autour du mot
      this.drawRectangleWord(
        this.foreCtx,
        x,
        y,
        15,
        height,
        teletext || boxing ? 'black' : 'rgba(0, 0, 0, 0.15)',
        'black'
      );

      this.showCursor(x, y, height);
      return;
    }

    let word = line.words[this.currentWordIndex];
    if (!word) {
      word = line.words[0];
      this.currentWordIndex = 0;
    }

    let letter = word.letters
      ? word.letters.find((x) => x.indexLetter === this.currentLetterIndex)
      : null;

    this.currentLine = line;
    this.currentWord = word;
    this.currentLetter = letter;

    let x = 0; // Coordonnée X de la ligne

    if (!word.letters || word.letters.length === 0) {
      x = word.startX;
    } else {
      if (!letter) {
        letter = last(word.letters);
        if (!letter) {
          return;
        }

        this.currentLetter = letter;
        this.currentLetterIndex = letter.indexLetter;
      }

      x = letter.x;
    }

    const startY = word.startY - this.lineHeight + metricY;

    this.foreCtx.font = `${this.fontSize}px ${teletext ? "Courier New" : fontName}`;

    // en cours de sélection
    if (this.underSelection) {
      return;
    }

    // Dessiner le rectangle autour du mot
    this.drawRectangleWord(
      this.foreCtx,
      word.startX,
      startY,
      word.width,
      height,
      teletext || boxing ? 'black' : 'rgba(0, 0, 0, 0.15)'
    );

    // Dessiner un rectangle sur les lettres si la recherche matche
    if (
      (this.props.search.showSearch || this.props.filtered) &&
      this.props.search.wordsToSearch.length
    ) {
      const wordFounded =
        this.props.search.wordsToSearch[this.props.search.currentPosition];
      if (wordFounded && wordFounded.id === this.subtitle.id) {
        const wordToSearch = wordFounded.words.find(
          (x) =>
            x.indexWord === word.indexWord
        );

        if (wordToSearch) {
          if (this.props.search.showSelection) {
            this.drawSearchWord(
              this.foreCtx,
              this.currentWord,
              wordToSearch.startIndex,
              wordToSearch.endIndex,
              '#3E79BC'
            );

            this.drawWord(this.foreCtx, this.currentWord);
            this.showCursor(x, startY, height);
            return;
          }

          this.drawSearchWord(this.foreCtx, this.currentWord);
        }
      }
    }

    // Redessiner le mot
    this.drawWord(this.foreCtx, word);

    // Dessiner le markword
    if (subtitle.mark && this.currentWord.indexWord === 0) {
      this.drawMarkWords(
        this.foreCtx,
        subtitle.mark,
        this.currentLine.startX,
        this.currentLine.startY
      );
    }

    // dessiner le curseur
    this.showCursor(x, startY, height);
  },

  // ***************************************************************************************
  // showCursor() permet d'afficher le curseur en continue (clignotant) ou non
  // ***************************************************************************************
  showCursor(x, y, height) {
    if (this.showCptCurs) {
      // Dessiner le curseur
      // En fonction du showCptCurs, on peut afficher le curseur de texte clignotant ou non
      this.cptCurs++;
      if (this.cptCurs < 50) {
        this.drawCursor(x, y, height);
      } else if (this.cptCurs > 100) this.cptCurs = 0;
    } else {
      this.drawCursor(x, y, height);
    }
  },

  // ***************************************************************************************
  // drawCursor()
  // Permet de dessiner le curseur
  // ***************************************************************************************
  drawCursor(x, y, height, color = 'red') {
    this.foreCtx.beginPath();
    this.foreCtx.moveTo(x, y); // Point de départ de la ligne
    this.foreCtx.lineTo(x, y + height); // Point d'arrivée de la ligne
    this.foreCtx.strokeStyle = color; // Couleur de la ligne
    this.foreCtx.lineWidth = 2; // Épaisseur de la ligne
    this.foreCtx.stroke();
  },

  // ***************************************************************************************
  // drawRectangleWord()
  // dessine un rectangle autour du mot (x, y, largeur, hauteur)
  // ***************************************************************************************
  drawRectangleWord(ctx, startX, startY, width, height, color, strokeColor = '#959595') {
    const rect = {
      x: startX - 2,
      y: startY,
      width: width - this.spaceWidth + 4,
      height: height,
    };
    ctx.beginPath();

    // Couleur de remplissage du rectange
    ctx.fillStyle = color;
    ctx.fillRect(rect.x, rect.y, rect.width, rect.height);

    // Ajouter une bordure autour du rectangle
    ctx.strokeStyle = strokeColor; // Couleur de bordure
    ctx.lineWidth = 2; // Largeur de la bordure
    ctx.roundRect(rect.x, rect.y, rect.width, rect.height, [3]);
    ctx.stroke();
  },

  // ***************************************************************************************
  // drawSelectionWord()
  // permet de dessiner la sélection des mots
  // ***************************************************************************************
  drawSelectionWord(ctx, word) {
    const rect = {
      x: word.startX - 4,
      y: word.startY - this.lineHeight + 6,
      width: word.width + 2,
      height: this.lineHeight,
    };

    const indexSelection = this.wordsToSelect.findIndex(
      (x) => x.indexWord === word.indexWord
    );
    const prevWord = this.wordsToSelect[indexSelection - 1];
    const nextWord = this.wordsToSelect[indexSelection + 1];

    const isFirstSelection =
      indexSelection === 0 ||
      (prevWord && prevWord.indexLine !== word.indexLine);
    const isLastSelection =
      indexSelection === this.wordsToSelect.length - 1 ||
      (nextWord && nextWord.indexLine !== word.indexLine);

    // Couleur de remplissage du rectangle
    ctx.fillStyle = '#595959';

    const radius = 3;
    ctx.beginPath();

    // Ajouter les coins arrondis en fonction de la sélection
    if (isFirstSelection && isLastSelection) {
      ctx.roundRect(rect.x, rect.y, rect.width, rect.height, [radius]);
    } else if (isFirstSelection) {
      ctx.roundRect(rect.x, rect.y, rect.width, rect.height, [
        radius,
        0,
        0,
        radius,
      ]);
    } else if (isLastSelection) {
      ctx.roundRect(rect.x, rect.y, rect.width, rect.height, [
        0,
        radius,
        radius,
        0,
      ]);
    } else {
      ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
    }

    ctx.fill();
  },

  // ***************************************************************************************
  // drawSelectionLetter()
  // permet de dessiner la sélection des letters
  // ***************************************************************************************
  drawSelectionLetter(ctx, letter) {
    const rect = {
      x: letter.x - 2,
      y: letter.y - this.lineHeight + 6,
      width: letter.width + 1,
      height: this.lineHeight,
    };

    // Couleur de remplissage du rectangle
    ctx.fillStyle = '#595959';
    ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
  },

  // ***************************************************************************************
  // drawMarkWords()
  // permet de marquer les mots dans le canvas
  // ***************************************************************************************
  drawMarkWords(ctx, mark, startX, startY) {
    const markWords = findMarkWords(mark);

    let iconX = startX;
    markWords.forEach((markWord) => {
      const icon = new Image();

      switch (markWord.key) {
        case 'CHECKWORD':
          icon.src = checkst;
          break;

        case 'NEWST':
          icon.src = newst;
          break;

        default:
          throw Error('icon not implemented');
      }

      const iconY = startY - 7;
      ctx.drawImage(icon, iconX, iconY, 12, 12);

      iconX += 12;
    });
  },

  // ***************************************************************************************
  // drawSearchWord()
  // permet de dessiner le mot recherché
  // ***************************************************************************************
  drawSearchWord(ctx, currentWord, startIndex, endIndex, color = '#9F6D0A') {
    ctx.fillStyle = color;

    if (!currentWord.letters.length 
      || !currentWord.letters[startIndex] 
      || !currentWord.letters[startIndex].x) 
      return;

    const startX = currentWord.letters[startIndex].x;
    let width = 0;

    for (let i = startIndex; i < endIndex; i++) {
      width += currentWord.letters[i].width;
    }

    const rect = {
      x: startX,
      y: currentWord.startY - this.lineHeight + 6,
      width: width,
      height: this.lineHeight,
    };

    ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
  },

  // ***************************************************************************************
  // drawSuggestErrors() permet d'afficher le mot en erreur
  // ***************************************************************************************
    drawSuggestErrors(ctx, word) {
    if (!word.suggest) {
      return;
    }

    const startX = word.startX;
    let endX = word.endX - this.spaceWidth;
    const endY = word.endY - 20;

    ctx.beginPath();
    ctx.moveTo(startX, endY);
    ctx.lineTo(endX, endY);

    ctx.strokeStyle = 'red';
    ctx.lineWidth = 2;
    ctx.stroke();
  },
};
